import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function Card({ title, children, highlight }) {
  return (
    <div className={`p-6 rounded-2xl border ${highlight ? "border-green-700 border-2" : "border-gray-200"} bg-white`}>
      <h3 className="text-lg font-bold text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function Billing() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [code, setCode] = useState("");
  const [codeMsg, setCodeMsg] = useState(null); // {ok, text}

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/billing/subscription`, { headers: authHeaders() });
      setSub(res.data);
    } catch (err) {
      console.error("Failed to load subscription:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const redeemCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy("code");
    try {
      const res = await axios.post(`${API}/billing/redeem-code`, { code: code.trim() }, { headers: authHeaders() });
      setCodeMsg({ ok: true, text: res.data.msg || "Code redeemed!" });
      setCode("");
      window.setTimeout(() => window.location.reload(), 1400);
    } catch (err) {
      setCodeMsg({ ok: false, text: err.response?.data?.error || "This code is invalid, expired, or already used." });
    } finally {
      setBusy("");
    }
  };

  const startTrial = async () => {
    setBusy("trial");
    try {
      await axios.post(`${API}/billing/start-trial`, {}, { headers: authHeaders() });
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || "Could not start trial");
      setBusy("");
    }
  };

  const subscribe = async () => {
    setBusy("subscribe");
    try {
      const res = await axios.post(`${API}/billing/create-checkout-session`, {}, { headers: authHeaders() });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      alert(res.data.message || "Could not open checkout");
    } catch (err) {
      alert(err.response?.data?.error || "Could not open checkout");
    } finally {
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const res = await axios.post(`${API}/billing/portal`, {}, { headers: authHeaders() });
      if (res.data.portal_url || res.data.url) {
        window.location.href = res.data.portal_url || res.data.url;
        return;
      }
      alert("Could not open the billing portal");
    } catch (err) {
      alert(err.response?.data?.error || "Could not open the billing portal");
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading your billing details…</div>;
  }

  // Derive the account's billing state
  const hasSub = sub?.has_subscription !== false && sub?.status;
  const isTrial = Boolean(sub?.is_trial);
  const daysLeft = sub?.days_until_trial_end || 0;
  const paidActive = sub?.status === "active" && Boolean(sub?.stripe_subscription_id);
  const compedActive = sub?.status === "active" && !sub?.stripe_subscription_id;
  const expired = Boolean(hasSub) && !sub?.is_active;

  const statusLine = compedActive
    ? sub?.plan === "free"
      ? "Free access"
      : "Lifetime / complimentary access"
    : paidActive
    ? "Subscription active"
    : isTrial
    ? `Free trial — ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`
    : expired
    ? "Access expired"
    : "No subscription yet";

  const statusDetail = compedActive
    ? "You have full access. Nothing to pay."
    : paidActive
    ? sub?.current_period_end
      ? `Next billing date: ${new Date(sub.current_period_end).toLocaleDateString()}`
      : ""
    : isTrial
    ? `Trial ends ${sub?.trial_end ? new Date(sub.trial_end).toLocaleDateString() : ""}. Redeem a code or subscribe below to keep access after that.`
    : expired
    ? "Your trial has ended. Redeem an access code or activate a subscription to continue."
    : "Start a free trial, redeem a code, or subscribe below.";

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-2xl font-bold text-primary mb-6">Billing &amp; Access</h2>

      {/* Status */}
      <div className={`p-6 rounded-2xl mb-8 border-2 ${expired ? "border-amber-400 bg-amber-50" : "border-green-700 bg-green-50"}`}>
        <div className="text-xl font-bold text-gray-900">{statusLine}</div>
        {statusDetail && <div className="text-sm text-gray-600 mt-1">{statusDetail}</div>}
      </div>

      {/* Three distinct paths */}
      <div className="grid md:grid-cols-3 gap-5">
        <Card title="Redeem an access code" highlight={expired}>
          <p className="text-sm text-gray-600 mb-4">
            Lifetime and 3-month codes (PLX-XXXX-XXXX) activate instantly — no card needed.
          </p>
          <form onSubmit={redeemCode} className="space-y-2">
            <input
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setCodeMsg(null);
              }}
              placeholder="PLX-XXXX-XXXX"
              className="w-full border rounded-lg px-3 py-2 uppercase"
            />
            <button
              type="submit"
              disabled={busy === "code" || !code.trim()}
              className="w-full px-4 py-2 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800 disabled:opacity-50"
            >
              {busy === "code" ? "Redeeming…" : "Redeem code"}
            </button>
            {codeMsg && (
              <p className={`text-sm ${codeMsg.ok ? "text-green-700" : "text-red-600"}`}>{codeMsg.text}</p>
            )}
          </form>
        </Card>

        <Card title="Free trial">
          {isTrial ? (
            <div>
              <p className="text-sm text-gray-600">
                Your trial is <strong>active</strong> — {daysLeft} day{daysLeft === 1 ? "" : "s"} remaining.
              </p>
              <p className="text-xs text-gray-400 mt-2">Every new account gets one 7-day trial.</p>
            </div>
          ) : hasSub ? (
            <p className="text-sm text-gray-500">
              Your trial has been used. Redeem a code or subscribe to continue.
            </p>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Try everything free for 7 days. No credit card required.
              </p>
              <button
                onClick={startTrial}
                disabled={busy === "trial"}
                className="w-full px-4 py-2 border-2 border-green-700 text-green-700 rounded-lg font-semibold hover:bg-green-50 disabled:opacity-50"
              >
                {busy === "trial" ? "Starting…" : "Start 7-day free trial"}
              </button>
            </div>
          )}
        </Card>

        <Card title="Subscription — $49/month" highlight={!compedActive && !paidActive}>
          {compedActive ? (
            <p className="text-sm text-gray-500">
              You have complimentary access — no subscription needed.
            </p>
          ) : paidActive ? (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Your subscription is active{sub?.cancel_at_period_end ? " (cancels at period end)" : ""}.
              </p>
              <button
                onClick={openPortal}
                disabled={busy === "portal"}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50"
              >
                {busy === "portal" ? "Opening…" : "Manage billing"}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Full access to the radar, payer intelligence, and referral CRM. Cancel anytime.
              </p>
              <button
                onClick={subscribe}
                disabled={busy === "subscribe"}
                className="w-full px-4 py-2 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800 disabled:opacity-50"
              >
                {busy === "subscribe" ? "Opening checkout…" : "Activate subscription"}
              </button>
              <p className="text-xs text-gray-400 mt-2">Secure checkout via Stripe.</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
